import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Table, Button, Badge, CopyButton, Code, Loader, Center,
  TextInput, Select, Alert, Avatar,
} from '@mantine/core';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconAdmin, IconAdd as IconPlus } from '../../components/icons/CncIcons';
import ImageUpload from '../../components/ImageUpload';
import { COUNTRY_NAMES, countiesFor } from '../../lib/geo';
import { api, imageUrl } from '../../api/client';
import { initialsOf } from '../../lib/initials';
import UserModal, { type AdminUser } from './UserModal';
import type { Technician } from '../technicians/TechniciansPage';

interface ServerInfo {
  lanIp: string | null;
  port: number;
  url: string;
  localUrl: string;
  dataRoot?: string;
  quotationsArchiveDir?: string;
}

interface CompanySettings {
  companyLogoPath: string | null;
  companyName: string | null;
  companyLegalName: string | null;
  companyAddressCountry: string | null;
  companyAddressCounty: string | null;
  companyAddressStreet: string | null;
  companyAddressZip: string | null;
}

const EMPTY_COMPANY: CompanySettings = {
  companyLogoPath: null,
  companyName: null,
  companyLegalName: null,
  companyAddressCountry: null,
  companyAddressCounty: null,
  companyAddressStreet: null,
  companyAddressZip: null,
};

export default function AdminPage() {
  const [info, setInfo] = useState<ServerInfo | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);
  // Per-request role selection for pending access requests (admin must pick).
  const [approveRole, setApproveRole] = useState<Record<number, string | null>>({});
  const [busyReq, setBusyReq] = useState<number | null>(null);

  // Company Details: draft state + last-known-persisted state.
  const [saved, setSaved] = useState<CompanySettings>(EMPTY_COMPANY);
  const [draft, setDraft] = useState<CompanySettings>(EMPTY_COMPANY);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyErr, setCompanyErr] = useState<string | null>(null);

  async function loadAll() {
    const [i, s, u, techs] = await Promise.all([
      api.get<ServerInfo>('/admin/server-info'),
      api.get<CompanySettings>('/admin/settings'),
      api.get<AdminUser[]>('/admin/users'),
      api.get<Technician[]>('/technicians'),
    ]);
    setInfo(i);
    setSaved(s);
    setDraft(s);
    setUsers(u);
    setTechnicians(techs);
  }
  useEffect(() => { loadAll(); }, []);

  async function approveRequest(u: AdminUser) {
    const role = approveRole[u.id];
    if (!role) return;
    setBusyReq(u.id);
    try {
      await api.post(`/admin/users/${u.id}/approve`, { role });
      notifications.show({ message: `${u.username} approved as ${role}.`, color: 'green' });
      await loadAll();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Approve failed', color: 'red' });
    } finally { setBusyReq(null); }
  }

  function rejectRequest(u: AdminUser) {
    modals.openConfirmModal({
      title: `Reject access request from ${u.username}?`,
      children: <Text size="sm">The request will be removed. They can request again by signing in with Microsoft.</Text>,
      labels: { confirm: 'Reject request', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${u.id}/reject`);
          notifications.show({ message: 'Request rejected.', color: 'gray' });
          await loadAll();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Reject failed', color: 'red' });
        }
      },
    });
  }

  const companyDirty = useMemo(() => {
    const k = Object.keys(draft) as Array<keyof CompanySettings>;
    return k.some((key) => (draft[key] ?? null) !== (saved[key] ?? null));
  }, [draft, saved]);

  function patchCompany(p: Partial<CompanySettings>) {
    setDraft((prev) => ({ ...prev, ...p }));
  }

  async function saveCompany() {
    setCompanyErr(null);
    setSavingCompany(true);
    try {
      const next = await api.put<CompanySettings>('/admin/settings', draft);
      setSaved(next);
      setDraft(next);
      notifications.show({ message: 'Company details saved.', color: 'green' });
    } catch (e: any) {
      setCompanyErr(e.message ?? 'Save failed');
    } finally {
      setSavingCompany(false);
    }
  }

  function cancelCompany() {
    setDraft(saved);
    setCompanyErr(null);
  }

  if (!info || !users) return <Center p="xl"><Loader /></Center>;

  const addressCounties = countiesFor(draft.companyAddressCountry);

  return (
    <Stack>
      <Group gap={10}><IconAdmin size={28} /><Title order={2}>Admin</Title></Group>

      <div className="section">
        <div className="section-header">Server Settings</div>
        <div className="section-body">
          <Stack gap="xs">
            <Group gap="xs" wrap="wrap">
              <Text size="sm" fw={500}>Network URL:</Text>
              <Code>{info.url}</Code>
              <CopyButton value={info.url}>
                {({ copied, copy }) => (
                  <Button size="compact-xs" variant="light" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
                )}
              </CopyButton>
              {!info.lanIp && <Badge color="red" variant="light">No LAN interface found — falling back to localhost</Badge>}
            </Group>
            <Text size="xs" c="dimmed">
              Share this URL with colleagues on the same Wi-Fi / wired network. The server binds to <Code>{info.localUrl}</Code> locally.
            </Text>
            {info.quotationsArchiveDir && (
              <Group gap="xs" wrap="wrap" mt="xs">
                <Text size="sm" fw={500}>Exported quotations archive:</Text>
                <Code>{info.quotationsArchiveDir}</Code>
                <CopyButton value={info.quotationsArchiveDir}>
                  {({ copied, copy }) => (
                    <Button size="compact-xs" variant="light" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
                  )}
                </CopyButton>
              </Group>
            )}
            {info.dataRoot && (
              <Text size="xs" c="dimmed">
                Data root: <Code>{info.dataRoot}</Code>. Each exported quotation lands at{' '}
                <Code>&lt;archive&gt;/&lt;quotation-UID&gt;.pdf</Code> and persists across server restarts.
              </Text>
            )}
          </Stack>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Company Details</div>
        <div className="section-body">
          <Stack>
            <ImageUpload
              entity="company"
              label="Company logo"
              value={draft.companyLogoPath}
              onChange={(p) => patchCompany({ companyLogoPath: p })}
            />

            <TextInput
              data-tour="admin-company-name"
              label="Company Name"
              value={draft.companyName ?? ''}
              onChange={(e) => patchCompany({ companyName: e.currentTarget.value || null })}
              maxLength={160}
            />
            <TextInput
              label="Company Legal Name"
              value={draft.companyLegalName ?? ''}
              onChange={(e) => patchCompany({ companyLegalName: e.currentTarget.value || null })}
              maxLength={160}
            />

            <Text size="sm" fw={600} mt="xs">Company Address</Text>
            <Select
              label="Country"
              placeholder="Select country"
              data={COUNTRY_NAMES}
              searchable
              clearable
              value={draft.companyAddressCountry}
              onChange={(v) => patchCompany({ companyAddressCountry: v, companyAddressCounty: null })}
            />
            {addressCounties ? (
              <Select
                label="County"
                placeholder="Select county"
                data={addressCounties}
                searchable
                clearable
                value={draft.companyAddressCounty}
                onChange={(v) => patchCompany({ companyAddressCounty: v })}
              />
            ) : (
              <TextInput
                label="County"
                placeholder={draft.companyAddressCountry ? 'Type county' : 'Select a country first'}
                value={draft.companyAddressCounty ?? ''}
                onChange={(e) => patchCompany({ companyAddressCounty: e.currentTarget.value || null })}
              />
            )}
            <TextInput
              label="Street Address"
              value={draft.companyAddressStreet ?? ''}
              onChange={(e) => patchCompany({ companyAddressStreet: e.currentTarget.value || null })}
              maxLength={200}
            />
            <TextInput
              label="ZIP Code"
              value={draft.companyAddressZip ?? ''}
              onChange={(e) => patchCompany({ companyAddressZip: e.currentTarget.value || null })}
              maxLength={20}
            />

            {companyErr && <Alert color="red">{companyErr}</Alert>}
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={cancelCompany} disabled={!companyDirty || savingCompany}>
                Cancel
              </Button>
              <Button data-tour="admin-company-save" onClick={saveCompany} loading={savingCompany} disabled={!companyDirty}>
                Save
              </Button>
            </Group>
          </Stack>
        </div>
      </div>

      {users.filter((u) => u.pending).length > 0 && (
        <div className="section" data-tour="admin-pending">
          <div className="section-header">Pending access requests ({users.filter((u) => u.pending).length})</div>
          <div className="section-body">
            <Text size="sm" c="dimmed" mb="sm">
              These Lumen users signed in with Microsoft but don’t have an account yet. Pick a role and approve, or reject.
            </Text>
            <Stack gap="sm">
              {users.filter((u) => u.pending).map((u) => (
                <Paper key={u.id} withBorder p="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={10} wrap="nowrap">
                      <Avatar src={imageUrl(u.imagePath, true)} radius="xl" size={40} color="green">
                        {initialsOf(u.displayName, u.username)}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>{u.displayName || u.username}</Text>
                        <Text size="xs" c="dimmed">{u.username}</Text>
                      </div>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Select
                        placeholder="role…"
                        data={['admin', 'editor', 'viewer']}
                        value={approveRole[u.id] ?? null}
                        onChange={(v) => setApproveRole((s) => ({ ...s, [u.id]: v }))}
                        w={130}
                      />
                      <Button data-tour="admin-approve" color="green" loading={busyReq === u.id} disabled={!approveRole[u.id]} onClick={() => approveRequest(u)}>Approve</Button>
                      <Button variant="light" color="red" onClick={() => rejectRequest(u)}>Reject</Button>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </div>
        </div>
      )}

      <div className="section" data-tour="admin-users">
        <div className="section-header">Users</div>
        <div className="section-body">
          <Paper withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Username</Table.Th>
                  <Table.Th style={{ width: 140 }}>Role</Table.Th>
                  <Table.Th>Display name</Table.Th>
                  <Table.Th style={{ width: 180 }}>Technician</Table.Th>
                  <Table.Th style={{ width: 180 }}>Last login</Table.Th>
                  <Table.Th style={{ width: 100 }}>Logins</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.filter((u) => !u.pending).map((u) => (
                  <Table.Tr key={u.id} className="click-row" onClick={() => setEditing(u)}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Avatar src={imageUrl(u.imagePath, true)} radius="xl" size={28} color="orange">
                          {initialsOf(u.displayName, u.username)}
                        </Avatar>
                        <Text>{u.username}</Text>
                        {u.entraLinked && <Badge size="xs" variant="light" color="blue">Entra</Badge>}
                        {u.mustChangePassword && <Badge size="xs" color="orange">Password reset</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td><Badge variant="light" color={u.role === 'admin' ? 'red' : u.role === 'editor' ? 'blue' : 'gray'}>{u.role}</Badge></Table.Td>
                    <Table.Td>{u.displayName ?? <Text span c="dimmed">—</Text>}</Table.Td>
                    <Table.Td>{u.technicianName ?? <Text span c="dimmed">—</Text>}</Table.Td>
                    <Table.Td>{u.lastLoginAt ? dayjs(u.lastLoginAt).format('YYYY-MM-DD HH:mm') : <Text span c="dimmed">—</Text>}</Table.Td>
                    <Table.Td>{u.loginCount}</Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Button data-tour="admin-add-user" fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
                      Add User
                    </Button>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        </div>
      </div>

      {editing && (
        <UserModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => { await loadAll(); setEditing(null); }}
          technicians={technicians}
        />
      )}
    </Stack>
  );
}
